const GREEN_ACCESSIBLE = "#1E7229";

const defaultTheme = {
    palette: {
        primary: {
            main: "#B8E1BB",
            contrastText: "#000000",
            50: "#EEF8EF",
            100: "#D4EDD6",
            200: "#B8E1BB",
            300: "#9BD49F",
            400: "#85CB8B",
            500: "#70C276",
            600: "#68BC6E",
            700: "#5DB463",
            800: "#53AC59",
            900: "#419F46",
            A100: "#F3FFF4",
            A200: "#C0FFC3",
            A400: "#8DFF93",
            A700: "#74FF7B"

        },
        secondary: {
            main: "#776E62",
            contrastText: "#FFF",
            50: "#F6F5F4",
            100: "#E8E6E3",
            200: "#D8D5D0",
            300: "#C8C4BD",
            400: "#BDB8AF",
            500: "#A39C91",
            600: "#9B9489",
            700: "#918A7E",
            800: "#888074",
            900: "#776E62",
            A100: "#FEF6EB",
            A200: "#FCE1BA",
            A400: "#FFCD85",
            A700: "#FFC26B"
        },
        error: {
            main: "#B22222",
            light: "#F8D0D1",
            danger: "#B22222"
        },
        black: "#000000",
        grey: {
            0: "#FFFFFF",
            50: "#E9E9E9",
            100: "#C8C8C8",
            200: "#A4A4A4",
            300: "#7F7F7F",
            400: "#636363",
            500: "#484848",
            600: "#414141",
            700: "#383838",
            800: "#303030",
            900: "#212121"
        },
        green: GREEN_ACCESSIBLE,
        backgrounds: {
            secondary: {
                lighter: "#E8E6E3",
                light: "#D8D5D0",
                dark: "#776E62"
            }
        },
        text: {
            primary: "rgba(0, 0, 0, 0.87)",
            secondary: "rgba(0, 0, 0, 0.87)",
            disabled: "#A4A4A4",
            white: "#FFFFFF",
            black: "#000000",
            medium: "#636363",
            link: "#404CE7",
            disabledStrong: "rgba(0, 0, 0, 0.38)",
        },
        background: {
            default: "#FFFFFF",
            white: "#FFFFFF",
            light: "#E9E9E9",
            dark: "#484848",
            primary: {
                lighter: "#EEF8EF",
                light: "#D4EDD6"
            }
        },
        borders: {
            line: {
                lighter: "#E8E6E3",
                light: "#D8D5D0"
            }
        },
        tonalOffset: 0.2,
        textOpacity: {
            textStrong: 1,
            textHigh: 0.87,
            textMedium: 0.6,
            textDisabled: 0.38,
            textHidden: 0
        }
    },
    typography: {
        useNextVariants: true,
        fontFamily: "AvenirNext",
        mixBlendMode: "normal",
        allVariants: {
            color: "rgba(0, 0, 0, 0.87)"
        },
        h1: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "38px",
            lineHeight: "115%",
            letterSpacing: "-0.5px",
            "@media (max-width: 600px)": {
                fontSize: "34px"
            }
        },
        h2: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "300",
            fontSize: "36px",
            lineHeight: "125%",
            letterSpacing: "-0.5px",
            "@media (max-width: 600px)": {
                fontSize: "30px"
            }
        },
        h3: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "32px",
            lineHeight: "125%",
            letterSpacing: "-0.5px",
            "@media (max-width: 600px)": {
                fontSize: "26px"
            }
        },
        h4: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "24px",
            lineHeight: "125%",
            "@media (max-width: 600px)": {
                fontSize: "22px"
            }
            // letterSpacing: "-0.5px"
        },
        h5: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "20px",
            lineHeight: "125%",
            letterSpacing: "0.25px"
        },
        h6: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "18px",
            lineHeight: "125%",
            // letterSpacing: "0.25px"
        },
        body1: {
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: "16px",
            lineHeight: "140%",
            // letterSpacing: "0.25px"
        },
        body2: {
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: "14px",
            lineHeight: "140%",
            letterSpacing: "0.05px"
        },
        subtitle1: {
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "16px",
            lineHeight: "125%",
            letterSpacing: "0.1px",
        },
        subtitle2: {
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "14px",
            lineHeight: "125%",
            letterSpacing: "0.1px",
        },
        button: {
            textTransform: "none",
            fontStyle: "normal",
            fontWeight: "600",
            fontSize: "14px",
            lineHeight: "75%",
            letterSpacing: "0.5px",
        },
        caption: {
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: "12px",
            lineHeight: "140%",
            letterSpacing: "0.1px",
        },
        overline: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "700",
            fontSize: "12px",
            lineHeight: "150%",
            letterSpacing: "2px",
            textTransform: "uppercase"
        },
        // TODO: this variant cannot be useed because it is not expected in the theme, thus invalid
        infoMessage: {
            fontFamily: "Josefin Sans, sans-serif;",
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: "18px",
            lineHeight: "140%",
        }
    },
    overrides: {
        MuiOutlinedInput: {
            root: {
                "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
                    // backgroundColor: "rgba(0,0,0,0.04)"
                },
                "&.Mui-focused:not($error) $notchedOutline": {
                    borderColor: GREEN_ACCESSIBLE
                },
            },
            input: {
                "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px white inset",
                },
                "&:disabled": {
                    color: "#7F7F7F"
                }
            }
        },
        MuiInputLabel: {
            root: {
                "&.Mui-focused:not(.Mui-error)": {
                    color: GREEN_ACCESSIBLE
                }
            }
        },
        MuiSelect: {
            select: {
                "&:focus": {
                    backgroundColor: "#F6F5F4 !important"
                },
                paddingTop: 12
            }
        },
        MuiPickersArrowSwitcher: {
            iconButton: {
                backgroundColor: "#FFF",
                color: "#70C276",
                "@media (max-width: 600px)": {
                    display: "none"
                }
            },
        },
        MuiPickersDay: {
            daySelected: {
                backgroundColor: "#70C276 !important",
                "@media (hover: none)": {
                    "&:hover": {
                        color: "#FFFFFF",
                        backgroundColor: "#5DB463 !important"
                    }
                },
                "@media (hover: hover)": {
                    "&:hover": {
                        color: "#FFFFFF",
                        backgroundColor: "#70C276 !important"
                    }
                },
            },
            dayDisabled: {
                color: "rgba(0, 0, 0, 0.12) !important",
            },
            day: {
                "&:hover": {
                    border: "1px solid #70C276 !important",
                    backgroundColor: "#EEF8EF !important",
                    color: "#212121"
                }
            }
        },
        MuiPickersDateRangeDay: {
            rangeIntervalDayHighlight: {
                backgroundColor: "#E9E9E9",
                color: "#FFF"
            },
        },
        MuiDialogTitle: {
            root: {
                padding: "16px 8px",
            }
        },
        MuiButton: {
            outlinedPrimary: {
                "@media (hover: hover)": {
                    "&:hover": {
                        background: "rgba(112, 194, 118, 0.08)",
                        border: "1px solid #D8D5D0",
                        boxSizing: "border-box",
                        borderRadius: "6px"
                    },
                    border: "1px solid #D8D5D0",
                    borderRadius: "6px",
                    color: GREEN_ACCESSIBLE,
                },
            },
            containedPrimary: {
                "@media (hover: hover)": {
                    "&:hover": {
                        background: "#5DB463",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 4px 5px rgba(0, 0, 0, 0.14) !important",
                        borderRadius: "6px"
                    },
                    borderRadius: "6px"
                },
            },
            textPrimary: {
                "@media (hover: hover)": {
                    "&:hover": {
                        background: "rgba(112, 194, 118, 0.08)",
                        borderRadius: "6px",
                    },
                    borderRadius: "6px",
                    color: GREEN_ACCESSIBLE,
                },
            },
            textSecondary: {
                "&:hover": {
                    background: "#776E62 !important",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 4px 5px rgba(0, 0, 0, 0.14) !important",
                    borderRadius: "6px"
                },
                borderRadius: "6px",
            },
            outlinedSecondary: {
                "&:hover": {
                    background: "#776E62 !important",
                    borderRadius: "6px"
                },
                borderRadius: "6px",
            },
            containedSecondary: {
                "&:hover": {
                    background: "#776E62 !important",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 4px 5px rgba(0, 0, 0, 0.14) !important",
                    borderRadius: "6px"
                },
                borderRadius: "6px",
            },
            iconSizeSmall: { // sizes of start icon & end icon
                "& > *:first-child": {
                    fontSize: "16px",
                    color: "inherit"
                },
            },
            iconSizeMedium: {
                "& > *:first-child": {
                    fontSize: "16px",
                    color: "inherit"
                },
            },
            iconSizeLarge: {
                "& > *:first-child": {
                    fontSize: "18px",
                    color: "inherit"
                },
            },
        },
        MuiIconButton: {
        },
        MuiContainer: {
            fixed: {
                "@media (min-width: 960px)": {
                    maxWidth: "none"
                },
                "@media (min-width: 600px)": {
                    maxWidth: "none"
                }
            }
        },
        MuiFormControlLabel: {
            label: {
                "&$disabled": {
                    color: "rgba(0, 0, 0, 0.32)", // TODO: replace all disabled throughout the app with this color?
                }
            }
        },
        MuiPickersDesktopDateRangeCalendar: {
            dateRangeContainer: {
                "@media (max-width: 600px)": {
                    display: "block",
                    flexDirection: "row"
                }
            },
            rangeCalendarContainer: {
                border: "none !important"
            },
            arrowSwitcher: {
                "@media (max-width: 600px)": {
                    paddingLeft: "32px !important"
                }
            }
        },
        MuiChip: {
            clickable: {
                "&:hover": {
                    color: "#776E62 !important",
                    background: "#FFFFFF !important",
                    border: "1px solid #A39C91",
                    boxSizing: "border-box",
                    borderRadius: "27px",
                }
            },
            deletable: {
                "&:hover": {
                    border: "1px solid #A39C91",
                    boxSizing: "border-box",
                    borderRadius: 27,
                    color: "#776E62 !important",
                    background: "#FFFFFF !important"
                },
                "&:focus": {
                    border: "1px solid #A39C91",
                    boxSizing: "border-box",
                    borderRadius: 27,
                    color: "#776E62 !important",
                    background: "#FFFFFF !important"
                }
            },
            deleteIcon: {
                color: "rgba(163, 156, 145, 0.76) !important",
                "@media (hover: hover)": {
                    "&:hover": {
                        color: "#776E62 !important"
                    },
                }
            }
        },
        MuiAlert: {
            outlinedInfo: {
                "& .MuiAlert-icon": {
                    color: "#918A7E",
                },
            }
        },
        MuiLink: {
            root: {
                // We want to target typography links of variant: body1-2, subtitle1-2
                "&> .MuiTypography-root": {
                    color: "#000000",
                    fontWeight: "500",
                    textDecoration: "underline",
                    cursor: "pointer"
                },
                "&> .MuiTypography-body1": {
                    fontWeight: "700",
                },
                "&> .MuiTypography-subtitle1": {
                    fontWeight: "700"
                },
                "&> .MuiTypography-subtitle2": {
                    fontWeight: "700"
                },
            },
        },
    }
};

export const hexToRgb = (hex, opacity) => {
    const bigint = parseInt(hex.replace("#", ""), 16);
    const output = [((bigint >> 16) & 255), ((bigint >> 8) & 255), (bigint & 255)];
    if (opacity !== undefined) {
        output.push(opacity);
    }
    return "rgba(" + output.join(", ") + ")";
};

export const getMUILanguage = (lang) => {
    let result = undefined;

    if (lang.startsWith("en")) {
        result = "enUS";
    }
    else if (lang.startsWith("de")) {
        result = "deDE";
    }
    else if (lang.startsWith("it")) {
        result = "itIT";
    }
    else if (lang.startsWith("fr")) {
        result = "frFR";
    }
    else if (lang.startsWith("nl")) {
        result = "nlNL";
    }

    return result;
};

export default defaultTheme;
